import React from 'react';
import { Link } from '@mm/components';
import { Routes } from '../../routes/top-level-routes';
import { Template } from './Template';

export const MasterServiceAgreement = () => (
  <div>
    <h1 style={{ marginBottom: '13px' }}>
      Customers.ai Inc Master Service Agreement
    </h1>
    <p>
      <b>Effective date: February 18, 2020&nbsp;&nbsp;</b>
    </p>
    <p>
      <b>Welcome</b>
    </p>
    <p>
      <span>
        Welcome to Customers.ai. Customers.ai is an online platform that allows
        developers, individuals, companies, and agencies to easily build, host,
        and manage chatbots (i.e., on mobile and web sites, including Facebook
        Messenger), and provides short message service (“SMS”) products,
        features, services and transmissions. These are offered by
      </span>
      <span>
        Customers.ai, Inc. (“Customers.ai,” “we”, “us”, “our” and similar
        terms). Please read on to learn the rules and restrictions that govern
        any user’s (“you”, “your”, “yourself” and similar terms) use of any or
        all of our website(s), products, services, programs, portals,
        applications, programming interfaces (i.e., our API), and
        telecommunications services, as may be modified, enhanced and updated
        from time to time, in any and all media (individually and collectively,
        the “Services”). Unless otherwise specified herein for a particular type
        of Services (e.g., Facebook Messenger or SMS), whenever we mention the
        “Services” herein, we mean to refer to any or all of our Services.
      </span>
    </p>
    <p>
      <b>Binding Contractual Terms and Conditions&nbsp;</b>
    </p>
    <p>
      <span>
        In order to make use of the Services, you must agree to be bound by this
        Master Service Agreement (the “Terms” or this “Agreement”) and by our{' '}
        <Link to={Routes.privacyPolicy.path}>Privacy Policy</Link>
      </span>
      <span>, which is incorporated herein by reference.&nbsp; </span>
      <span>
        &nbsp;To the extent that Customers.ai processes any personal data of
        EU/EEA residents provided by its customers, and which personal data is
        subject to the General Data Protection Regulation (GDPR), the terms and
        conditions of Customers.ai’s Data Processing Addendum (“DPA”) at{' '}
        <Link to={Routes.gdpr.path}>{Routes.gdpr.getLink()} </Link>
      </span>
      <span>
        &nbsp;shall also apply to such processing on the customer’s
        behalf.&nbsp;
      </span>
    </p>
    <p>
      <span>
        If you object to anything in these Terms, the Privacy Policy, the
        Copyright Dispute Policy, or the DPA, you are prohibited from using the
        Services.
      </span>
    </p>
    <p>
      <span>
        These Terms, the Privacy Policy, the Copyright Dispute Policy, and the
        DPA are subject to change by us at any time, effective upon posting
        through the Services. By accessing the Services after any such change,
        you accept the Terms, the Privacy Policy, the Copyright Dispute Policy,
        and the DPA as modified.
      </span>
    </p>
    <p>
      <span>
        These Terms, all incorporated documents and policies, along with any
        online order form or other order form (“Order Confirmation”) are a
        binding contract between you and Customers.ai. You represent that you
        have all necessary power and authority to enter into these terms and
        bind yourself and/or any entity or organization that you are acting on
        behalf of. If you have any questions, comments, or concerns regarding
        these Terms or the Services, please contact us at tos@customers.ai, 359
        Newbury Street, 5th floor, Boston MA 02115.
      </span>
    </p>
    <p>
      <b>Customer Information</b>
    </p>
    <p>
      <span>
        “Customer Information” consists of data and other information made
        available to us by or for you through the use of the Services under
        these Terms. You agree that we have the right to use and disclose
        Customer Information as necessary to (a) provide the Services consistent
        with these Terms, including detecting, preventing, and investigating
        security incidents, fraud, spam, or unlawful use of the Services, and
        (b) respond to any technical problems or your queries and ensure the
        proper working of the Services.&nbsp; You warrant and represent to
        Customers.ai that you have all rights, title, and interests necessary to
        provide such information to us and that your provision of the
        information to us does not infringe any third party’s proprietary or
        personal rights, including, but not limited to, any trademark,
        copyright, patent, or trade secret, or violate any applicable laws,
        rules or regulations. You acknowledge that chatbot messaging and SMS are
        an insecure medium and security of information transmitted through the
        Services can never be guaranteed and, accordingly, we are not
        responsible for any interception or interruption of any communications
        through the Services or for changes to or loss of Customer Information
        in connection with the Services.
      </span>
    </p>
    <p>
      <b>Special Considerations for Chatbots on Facebook Messenger&nbsp;</b>
    </p>
    <p>
      <span>
        Your use of the Services connected to Facebook Messenger is also
        governed by and subject to the Facebook Platform Policies (currently
        located at https://developers.facebook.com/policy) and Facebook Commerce
        Product Merchant Agreement (currently located at
        https://www.facebook.com/legal/commerce_product_merchant_agreement),
        which are hereby incorporated by reference and are a part of these
        Terms. You are solely responsible and liable for complying with the
        Facebook Platform Policies and Facebook Commerce Product Merchant
        Agreement. You must agree to and accept all of the Terms, or you don’t
        have the right to use the Services. Your using the Services in any way
        means that you agree to all of these Terms, and these Terms will remain
        in effect while you use the Services.&nbsp;
      </span>
    </p>
    <p>
      <b>
        Special Considerations for SMS Services: Your Responsibility for Legal
        Compliance; Our Limited Responsibility
      </b>
    </p>
    <p>
      <i>
        <span>Legal and Other Compliance Issues</span>
      </i>
    </p>
    <p>
      <span>
        You are required to use the SMS Services in full compliance with all
        applicable laws, rules and regulations, including without limitation,
        all local, state, federal and foreign laws, rules and regulations. This
        includes but is not limited to the Telephone Consumer Protection Act, as
        amended (“TCPA”), the Federal Trade Commission rules, Federal
        Communications Commission rules, the National DNC list registry rules,
        and various other laws, rules and regulations which place restrictions
        on certain types of phone calls and text messages. Without limiting the
        foregoing, you agree to familiarize yourself with, and at all times
        comply with, all of the foregoing and any other relevant legal
        requirements triggered by any messages, calls, broadcasts, and campaigns
        transmitted through our SMS Services. You are solely responsible for all
        acts or omissions that occur under your account, including, without
        limitation, the content of the messages that you create and initiate
        through our Services. You understand that in some cases, applicable
        state and local restrictions are more restrictive than the federal
        rules. You should review any and all applicable laws, rules and
        regulations with your own legal counsel to ensure that you understand
        and comply.&nbsp; Customers.ai will not assume responsibility for
        ensuring that your activities meet applicable legal requirements.
        Customers.ai will not assume any liability if you are ever held guilty
        or found liable for any law violation. Also, in order to confirm your
        compliance with these Terms, we may, from time to time, periodically
        check the messages and broadcasts that you send.
      </span>
    </p>
    <p>
      <span>
        You represent and warrant that the owners of the phone numbers you
        initiate messages or broadcasts to through our SMS Services have
        consented or otherwise opted-in to the receipt of such messages as
        required by any applicable law, rule or regulation. You agree that you
        will include clear opt-out/unsubscribe information in your messages or
        broadcasts when required to do so by any applicable law, rule or
        regulation, and that you will otherwise adhere to the Consumer Best
        Practices Guidelines promulgated by the Mobile Marketing Association, if
        it is applicable to your messages.
      </span>
    </p>
    <p>
      <span>
        You will not attempt to use our SMS Services to access or allow access
        to any so-called “Emergency Services.”
      </span>
    </p>
    <p>
      <span>
        You represent and warrant that if you record or monitor SMS messages or
        other communications using the Services, then you will comply with all
        applicable laws prior to doing so and will secure all required prior
        consents to record or monitor communications using the Services. We make
        no representations or warranties with respect to recording or monitoring
        of SMS messages or other communications.&nbsp;
      </span>
    </p>
    <p>
      <span>
        We are in no way attempting to interpret any laws, rules, or
        regulations. This information is provided merely as a courtesy, and it
        is not intended to replace your responsibility to familiarize yourself
        with and abide by the legal requirements pertaining to your messages and
        campaigns prior to using the SMS Services. You are ultimately
        responsible to make your own informed decisions regarding your messages
        and campaigns.
      </span>
    </p>
    <p>
      <i>
        <span>Availability; Routes; Networks; Timing</span>
      </i>
    </p>
    <p>
      <span>
        Our Services may or may not be available in all countries or regions of
        the world, may be available under different names in different
        countries, and, where applicable, may be approved or cleared by a
        government regulatory body for sale or use with different indications
        and restrictions in different countries.
      </span>
    </p>
    <p>
      <span>
        We transmit messages initiated by you or sent to you by others through
        different routes, and the level of reliability and support for special
        features varies according to the route. You are responsible for
        obtaining and maintaining all computer hardware, software, and
        communications equipment needed to access the Services and for paying
        all access charges (e.g., Internet service provider, telecommunications)
        incurred while using the Services.
      </span>
    </p>
    <p>
      <span>
        We only attempt to transmit messages to the applicable third party
        network. We are not responsible for the final delivery of any
        communication initiated through our SMS Services, as this is out of our
        control and is the responsibility of downstream communications carriers.
      </span>
    </p>
    <p>
      <span>
        We transmit and receive messages via other major telecommunications
        companies and mobile network operators, and thus our influence over the
        timing of the transmission of your messages is only within the technical
        constraints imposed upon Customers.ai. While we shall use commercially
        reasonable efforts to transmit your messages to the applicable network
        for final delivery to your designated recipients as fast as possible, we
        cannot commit to, and do not guarantee, a specific maximum delivery
        time. Such times depend on various network and system-related factors
        among the various entities involved in the transmission of your messages
        across the public switched telephone network and/or Internet. You should
        know that communications carriers assign messages with a default
        lifetime and any message that cannot be delivered successfully within
        the lifetime assigned to it will be discarded by the communications
        carrier without any notice. We are not liable for any loss incurred by
        the failure of a message to be delivered, and you acknowledge that
        damages for financial or other loss resulting from delivery failure
        cannot be claimed from Customers.ai for any such non-deliveries.
        Furthermore, you agree that message contents are deemed to have zero
        value.
      </span>
    </p>
    <p>
      <i>
        <span>Other Requirements</span>
      </i>
    </p>
    <p>
      <span>
        We reserve the right to reclaim any phone number from your account and
        to return that number to the relevant numbering plan if you do not send
        sufficient traffic over that phone number, such that the phone number is
        unutilized or underutilized, as defined by any local or federal
        regulatory agency and/or governmental organization with oversight over
        the relevant phone number and numbering plan. When reclaiming unutilized
        or underutilized phone numbers, we will notify you at least fourteen
        (14) days in advance, unless we are prevented from doing so by the
        relevant carrier regulatory agency or governmental organization.
      </span>
    </p>
    <p>
      <span>
        We also reserve the right to reclaim phone numbers with no notice to you
        if your account is suspended for failure to pay or for suspected fraud,
        as well as if your free trial account is unutilized for more than thirty
        (30) days.
      </span>
    </p>
    <p>
      <span>
        You acknowledge that Customers.ai is the “customer of record” for all
        phone numbers provided as part of our Services. As the customer of
        record, we have certain rights with respect to porting phone numbers.
        You understand and agree that you may use the phone numbers provided as
        part of our Services subject to these Terms. Unless otherwise required
        by law, we reserve the right to refuse to allow you to port away any
        phone number in our sole discretion.
      </span>
    </p>
    <p>
      <b>License Grant &amp; Restrictions&nbsp;</b>
    </p>
    <p>
      <span>
        Upon payment of the fees charged by us for your use of the Services,
        Customers.ai hereby grants you a non-exclusive, non-transferable,
        limited, revocable, worldwide right to use the Services solely during
        the term for which you have paid for such Services, and solely for your
        own internal business purposes, subject to the terms and conditions of
        this Agreement.&nbsp; In the event that your Order Confirmation provided
        for a specific number of end-users of the Services (“Users”), your
        license to use the Services is limited to the specified number of
        Users.&nbsp; All rights not expressly granted to you are reserved by
        Customers.ai and its parents, subsidiaries, affiliates and/or
        licensors.&nbsp; You may not access the Services for purposes of
        monitoring its availability, performance or functionality, or for any
        other benchmarking or other competitive purposes. You will not reverse
        engineer, decompile, disassemble, derive, or otherwise create, attempt
        to derive or create, or assist anyone else to derive or create, the
        source code of any software provided in connection with our Services.
        You shall not: (i) license, sublicense, sell, resell, transfer, assign,
        distribute or otherwise commercially exploit or make available to any
        third party the Services, or the platform, software, technology,
        functionality, infrastructure, methods, know-how or other intellectual
        property included within or otherwise underlying such Services
        (“Customers.ai Technology”), or any of the names, the logos, designs,
        copyrights, service marks, trademarks, trade dress, or other
        intellectual property used or associated with the Services (the
        “Customers.ai Content”) in any way; (ii) modify or make derivative works
        based upon the Services, Customers.ai Technology or the Customers.ai
        Content; (iii) embed the Services as an “iframe” or “frame” from within
        another application; or (iv) reverse engineer or access the Services in
        order to (a) build a competitive product or service, (b) build a product
        using ideas, features, functions or graphics that are similar to those
        related to the Services, or (c) copy any ideas, features, functions or
        graphics of the Services. The Services cannot be shared or used by more
        than one individual unless (and solely to the extent that) the Order
        Confirmation allows for sharing within your internal organization.&nbsp;
        Sharing of the Services outside your organization is never
        allowed.&nbsp; The proper assignment of username and passwords for the
        Services and adherence to all terms of this Agreement and Order
        Confirmation are your sole responsibility. You are responsible for
        maintaining the confidentiality of your usernames, passwords and
        accounts.&nbsp; Usernames and passwords may not be shared by more than
        one individual, and may not be transferred from one individual to
        another unless the original User no longer requires, and is no longer
        permitted, access to the Services.&nbsp; You may use the Services only
        for your internal business purposes and shall not: (i) send or store
        material containing software viruses, worms, Trojan horses or other
        harmful computer code, files, scripts, agents or programs; (ii)
        interfere with or disrupt the integrity or performance of the Services
        or the data contained therein, or Customers.ai’s sites, servers or
        networks; (iii) attempt to gain unauthorized access to the Services or
        its related systems or networks; or (iv) take any action that imposes an
        unreasonably or disproportionately large load on Customers.ai’s or its
        service providers’ infrastructure.&nbsp; Customers.ai shall be entitled
        to adjust the scope of the Services and the underlying technical
        infrastructure to reflect the continuing development of the Services and
        technical advances.
      </span>
    </p>
    <p>
      <b>Intellectual Property Ownership</b>
    </p>
    <p>
      <span>
        Customers.ai alone (and its licensors, where applicable) shall own all
        right, title and interest, including all related intellectual property
        rights, in and to (i) the Customers.ai Technology, the Customers.ai
        Content and the Services, including but not limited to any
        modifications, enhancements, improvements, applications, registrations,
        and derivative works thereof, (ii) any suggestions, ideas, requests,
        feedback, recommendations or other information provided by you or any
        other party relating to the Services, Customers.ai Technology, and
        Customers.ai Content, and (iii) any aggregated metrics, data,
        information, materials, and trends compiled by Customers.ai
        (collectively, all of the foregoing in subclauses (i), (ii), and (iii)
        shall be defined herein as the “Intellectual Property Rights”). This
        Agreement is not a sale and does not convey to you any rights of
        ownership in or related to the Intellectual Property Rights. You are not
        granted a license to use the Intellectual Property Rights except as
        otherwise specifically provided within the Order Confirmation or this
        Agreement.&nbsp; The websites connected to the Services may include
        trademarks, service marks or logos of third parties, all of which are
        the property of their respective owners.&nbsp; In addition, all content
        published on the such sites belongs to Customers.ai and/or its licensors
        under applicable copyright law.
      </span>
    </p>
    <p>
      <b>Use of the Services&nbsp;</b>
    </p>
    <p>
      <span>
        Subject to the terms and conditions of this Agreement, you are being
        granted a limited right to use our Services in commerce as a means to
        resell goods and services which you use or offer commercially
        (including, with respect to the chatbot Services, the right to build
        chatbots using the Services and sell and/or otherwise exploit and
        commercialize the chatbots you have built), with the exception of cases
        in which your product is materially similar or designed to compete
        directly with the Services offered by Customers.ai.
      </span>
    </p>
    <p>
      <span>
        You will only use the Services in a manner that complies with all laws
        that apply to you. If your use of the Services is prohibited by
        applicable laws, then you aren’t authorized to use the Services. We
        can’t and won’t be responsible for your using the Services in a way that
        breaks the law or any misuse of the Services. You will not share your
        account or password with anyone, and you must protect the security of
        your account and your password. You are responsible for any activity
        associated with your account.
      </span>
    </p>
    <p>
      <span>
        Our Services promote sharing of personal information among Users. We do
        not and cannot assure that it is safe for you to have direct contact
        with any other individual that you come into contact with through the
        Services. If you believe that any individual is harassing you or is
        otherwise using personal information about you for unlawful purposes, we
        encourage you to first inform local law&nbsp;enforcement authorities and
        then to contact us at&nbsp;TOS@Customers.ai, so that we may take
        appropriate action to block further use of the Services by any
        individuals who may have accessed them for improper purposes.
      </span>
    </p>
    <p>
      <span>
        We may offer features that are based on the location of Users and which
        may report on those&nbsp;Users’ current locations (the “Location-Based
        Services”). Use of Location-Based Services is solely at your
        discretion.&nbsp;Should you use Location-Based Services, you consent to
        collection and dissemination of your location information
        through&nbsp;the Services. Under no circumstances shall Customers.ai,
        its parents, subsidiaries, affiliates, and licensors, and their
        respective directors, officers, employees, agents, and successors and
        assigns (“Customers.ai Parties”),&nbsp; be liable for claims or for any
        damages therefrom, arising out of your&nbsp;dissemination of your
        location information together with your profile information through the
        Services.
      </span>
    </p>
    <p>
      <span>
        We are not responsible for the views and opinions contained in any of
        your messages. We are transmitting them under the assumption that you
        are using the Services in accordance with these Terms. Opinions, votes,
        advice, statements, offers, or other information or content made
        available&nbsp;through the Services are those of their respective
        authors and should not necessarily be relied upon. Such authors
        are&nbsp;solely responsible for such content. We do not: (i) guarantee
        the accuracy, completeness, or usefulness of any&nbsp;information on the
        Services or (ii) adopt, endorse or accept responsibility for the
        accuracy or reliability of any&nbsp;opinion, advice, or statement made
        by any party that appears on the Services. Under no circumstances will
        we or our&nbsp;affiliates be responsible for any loss or damage
        resulting from your reliance on information or other content
        posted&nbsp;through the Services or transmitted to or by any Users.
      </span>
    </p>
    <p>
      <b>Customers.ai Code of Conduct</b>
    </p>
    <p>
      <span>
        By accessing the Services, you agree to the following code of conduct:
      </span>
    </p>
    <ul>
      <li>
        <span>
          You will not publish, display, transmit or use (hereinafter “post”)
          any unlawful, defamatory, abusive, obscene, profane, threatening,
          harassing, demeaning, immoral, harmful, vulgar, pornographic, racially
          offensive, discriminatory or otherwise offensive or illegal material.
        </span>
        <span>&nbsp;</span>
      </li>
      <li>
        <span>You will treat all fellow Users with dignity and respect.</span>
      </li>
      <li>
        <span>
          You will notify us promptly of any User’s violation of these Terms.
        </span>
      </li>
      <li>
        <span>
          Any information posted by you in any message or on any site, or
          contained in your Facebook profile and made accessible to other
          Users—including any telephone numbers, street addresses, last names,
          URLs, email addresses or other contact information—is posted at your
          own risk&nbsp;and we assume no responsibility for use of such
          information.
        </span>
      </li>
      <li>
        <span>You will not impersonate any person or entity.</span>{' '}
        <span>
          You will not mislead others as to the identity of the sender of your
          messages by creating a false identity, impersonating the identity of
          someone/something else, or by providing contact details that do not
          belong to you.
        </span>
      </li>
      <li>
        <span>
          You will not “stalk” or otherwise harass any User or other individual
          accessing the Services.
        </span>
      </li>
      <li>
        <span>
          You will not express or imply that any statements you make are
          endorsed by us without specific prior written consent.
        </span>
      </li>
      <li>
        <span>
          You will not use manual or automatic processes to retrieve, index,
          datamine, or in any way reproduce or circumvent&nbsp;the navigational
          structure or presentation of the Services or their contents. This
          prohibition does not include standard&nbsp;indexing by Internet search
          engines, which hereby is explicitly permitted.&nbsp;
        </span>
      </li>
      <li>
        <span>
          You will not post, distribute, transmit, or reproduce in any way any
          copyrighted material, trademarks, or other
          proprietary&nbsp;information, or information that may infringe upon
          the intellectual property rights or other rights of third parties
          (including rights of publicity), without obtaining the prior consent
          of the owner of such proprietary rights.
        </span>
      </li>
      <li>
        <span>
          You will not send unsolicited marketing messages or broadcasts (i.e.,
          spam).
        </span>
      </li>
      <li>
        <span>
          You will not send any prohibited calls to life-line services, such as
          hospitals, fire, police, 911, or utility- related telephone numbers.
        </span>
      </li>
      <li>
        <span>
          You will not use strings of numbers, as it may be unlawful to engage
          two or more lines of a multi-line business.
        </span>
      </li>
      <li>
        <span>
          You will not harvest or otherwise collect information about others
          without their consent.
        </span>
      </li>
      <li>
        <span>
          You will not transmit any material that contains viruses, trojan
          horses, worms, time bombs, cancel-bots, or any other harmful or
          deleterious programs.
        </span>
      </li>
      <li>
        <span>
          You will not interfere with, or disrupt, networks connected to the
          Services or violate the regulations, policies, or procedures of such
          networks.&nbsp;
        </span>
      </li>
      <li>
        <span>
          You will not interfere with another’s use and enjoyment of our
          Services.
        </span>
      </li>
      <li>
        <span>
          You will not engage in any other activity that we believe could
          subject us to criminal or civil liability.
        </span>
      </li>
    </ul>
    <p>
      <b>Responsibility &amp; Liability</b>
    </p>
    <p>
      <span>
        Any information or content publicly posted or privately transmitted
        through the Services by you or third parties and any chatbots or SMS
        messages created using the Services (collectively, the “Message
        Content”), are the sole responsibility of the person from whom such
        Message Content originated, and you use and access all such Message
        Content at your own risk, and the Customers.ai Parties are not liable
        for any errors or omissions in that Message Content or for any damages
        or loss you might suffer in connection with it.&nbsp;
      </span>
    </p>
    <p>
      <span>
        We cannot control and have no duty to take any action regarding how you
        may interpret and use the Message Content or what actions you may take
        as a result of having been exposed to the Message Content, and you
        hereby release the Customers.ai Parties from all liability for you
        having acquired or not acquired Message Content through the Services. We
        can’t guarantee the identity of any users with whom you interact in
        using the Services and are not responsible for which users gain access
        to the Services.&nbsp;
      </span>
    </p>
    <p>
      <span>
        You are responsible for all Message Content you contribute, in any
        manner, to the Services, and you represent and warrant you have all
        rights necessary to do so, in the manner in which you contribute it. You
        will keep all your registration information accurate and current. You
        are responsible for all your activity in connection with the
        Services.&nbsp;
      </span>
    </p>
    <p>
      <span>
        The Services may contain links or connections to third party websites or
        services that are not owned or controlled by Customers.ai. When you
        access third party websites or use third party services, you accept that
        there are risks in doing so, and that Customers.ai is not responsible
        for such risks. We encourage you to be aware when you leave the Services
        and to read the terms and conditions and privacy policy of each third
        party website or service that you visit or utilize.&nbsp;
      </span>
    </p>
    <p>
      <span>
        Customers.ai has no control over, and assumes no responsibility for, the
        content, accuracy, privacy policies, or practices of or opinions
        expressed in any third party websites or by any third party that you
        interact with through the Services. In addition, Customers.ai will not
        and cannot monitor, verify, censor or edit the content of any third
        party site or service. By using the Services, you release and hold the
        Customers.ai Parties harmless from any and all liability arising from
        your use of any third party website or service. Your interactions with
        organizations and/or individuals found on or through the Services,
        including payment and delivery of goods or services, and any other
        terms, conditions, warranties or representations associated with such
        dealings, are solely between you and such organizations and/or
        individuals. You should make whatever investigation you feel necessary
        or appropriate before proceeding with any online or offline transaction
        with any of these third parties. You agree that the Customers.ai Parties
        shall not be responsible or liable for any loss or damage of any sort
        incurred as the result of any such dealings. If there is a dispute
        between participants on this site, or between users and any third party,
        you agree that Customers.ai is under no obligation to become involved.
        In the event that you have a dispute with one or more other users, you
        release the Customers.ai Parties from claims, demands, and damages of
        every kind or nature, known or unknown, suspected or unsuspected,
        disclosed or undisclosed, arising out of or in any way related to such
        disputes and/or our Services.&nbsp;
      </span>
    </p>
    <p>
      <span>
        If you are a California resident, you shall and hereby do waive
        California Civil Code Section 1542, which says: “A general release does
        not extend to claims which the creditor does not know or suspect to
        exist in his or her favor at the time of executing the release, which,
        if known by him or her must have materially affected his or her
        settlement with the debtor.”
      </span>
    </p>
    <p>
      <b>Billing Policies</b>
    </p>
    <p>
      <span>
        Customers.ai is continually adding new products and services to its
        offering and offers a variety of payment plan and options. The following
        describes the general payment terms, and you hereby consent to such
        terms:
      </span>
    </p>
    <ul>
      <li>
        <span>
          For paying users, the service is billed in advance on a monthly basis
          based on the “start date” of a subscription and is
          non-refundable.&nbsp;
        </span>
      </li>
      <li>
        <span>
          There will be no refunds or credits for partial months of service,
          upgrade/downgrade refunds, or refunds for months unused with an open
          account unless otherwise agreed to in writing.
        </span>
      </li>
      <li>
        <span>
          Services fees are charged in accordance with the fees, charges, and
          billing terms in effect at the time a fee or charge is due and
          payable.&nbsp;
        </span>
      </li>
      <li>
        <span>
          The initial charges at sign up will be equal to the current list price
          of the chosen edition of the Services plan selected plus any add-ons
          selected and or discounts applied.
        </span>
      </li>
      <li>
        <span>
          Subsequent monthly payments may have add-on services or usage charges
          applied which are described in your monthly online billing statement.
        </span>
      </li>
      <li>
        <span>
          All fees are exclusive of all taxes, levies, or duties imposed by
          taxing authorities, and you shall be responsible for payment of all
          such taxes, levies, or duties.
        </span>
      </li>
      <li>
        <span>
          Cancellation of a paid subscription must be made within 7 days of your
          billing date in order to stop payment for the next month of service.
        </span>
      </li>
    </ul>
    <p>
      <b>Validation</b>
    </p>
    <p>
      <span>
        You must provide Customers.ai with a valid credit card as a condition to
        signing up for the Services.&nbsp; You hereby represent that you are
        authorized to provide any credit card you use to sign up for the
        Services.&nbsp; If the contact information you have provided is false or
        fraudulent, Customers.ai reserves the right to terminate your access to
        the Services in addition to any other legal remedies. All invoices and
        payments to Customers.ai will be in United States Dollars ($USD).&nbsp;
        If you believe your invoice is incorrect, you must contact Customers.ai
        in writing within 45 days of the date of the invoice setting forth
        details regarding the inaccuracy to be eligible to receive an adjustment
        or credit.
      </span>
    </p>
    <p>
      <b>Auto Renewal</b>
    </p>
    <p>
      <span>
        Customers.ai will automatically renew your monthly or annual
        subscription and bill your credit card or submit electronic invoices as
        mutually agreed upon.&nbsp;
      </span>
    </p>
    <p>
      <b>Non-Payment and Suspension</b>
    </p>
    <p>
      <span>
        In addition to any other rights granted to Customers.ai herein,
        Customers.ai reserves the right to suspend or terminate this Agreement
        and your access to the Services if your account becomes delinquent
        (falls into arrears). Delinquent invoices (accounts in arrears) are
        subject to interest of 1.5% per month on any outstanding balance, or the
        maximum rate permitted by law, whichever is less, plus all expenses of
        collection. You will continue to be charged for the Services during any
        period of suspension. If you or Customers.ai initiates termination of
        this Agreement, you will be obligated to pay the balance due on your
        account for the applicable initial term or renewal term, as the case may
        be, computed in accordance with this Agreement. You agree that
        Customers.ai may charge such unpaid fees to your credit card or
        otherwise bill you for such unpaid fees. Customers.ai reserves the right
        to impose a reconnection fee in the event you are suspended and
        thereafter request access to the Services. You agree and acknowledge
        that Customers.ai has no obligation to retain any data or content of its
        customers and that such data and/or content may be irretrievably deleted
        if your account is 30 days or more delinquent.
      </span>
    </p>
    <p>
      <b>Termination for Cause</b>
    </p>
    <p>
      <span>
        Any breach of your payment or other material obligations or unauthorized
        use of the Intellectual Property Rights or Services will be deemed a
        material breach of this Agreement. Without limiting its ability to
        pursue other remedies, Customers.ai, in its sole discretion, may
        terminate your password, account or use of the Services if you breach or
        otherwise fail to comply with this Agreement.&nbsp;
      </span>
    </p>
    <p>
      <b>Warranty Disclaimer and Limitations of Liability&nbsp;</b>
    </p>
    <p>
      <span>
        Neither Customers.ai nor its licensors or suppliers makes any
        representations or warranties concerning any content contained in or
        accessed through the Services, and we will not be responsible or liable
        for the accuracy, copyright compliance, legality, or decency of material
        contained in or accessed through the Services. We (and our licensors and
        suppliers) make no representations or warranties regarding suggestions
        or recommendations of services or products offered or purchased through
        the Services. Customers.ai will not be liable or responsible for failure
        or delay in fulfilling or performing its obligations if such failure or
        delay is caused by or results from causes beyond its reasonable control
        (including fires, floods, embargoes, shortages, epidemics, quarantines,
        acts of war (whether declared or not) or terrorism, insurrections,
        riots, civil commotion, failure of the internet, strikes, lockouts or
        other labor disturbances, acts of God or acts, omissions or delays of
        any governmental authority).
      </span>
      <span>&nbsp;</span>
    </p>
    <p>
      <span>
        CUSTOMERS.AI DISCLAIMS ALL WARRANTIES RELATED TO THIRD PARTY INTERNET
        AND/OR TELECOMMUNICATIONS PROVIDERS.&nbsp; CUSTOMERS.AI WILL NOT BE
        LIABLE AND WILL HAVE NO OBLIGATION TO INDEMNIFY YOU FOR SENSITIVE DATA
        THAT YOU SEND OR THAT ANY END USER SENDS TO YOU.
      </span>
    </p>
    <p>
      <span>
        NEITHER CUSTOMERS.AI NOR ITS REPRESENTATIVES WILL BE LIABLE UNDER ANY
        LEGAL OR EQUITABLE THEORY FOR ANY CLAIM, DAMAGE, OR LOSS (AND YOU WILL
        HOLD US HARMLESS AGAINST ANY AND ALL SUCH CLAIMS) ARISING FROM OR
        RELATING TO THE INABILITY TO USE THE SERVICES TO CONTACT ANY SO-CALLED
        EMERGENCY SERVICES. OUR OUTBOUND COMMUNICATIONS SERVICES SHOULD NOT BE
        USED FOR CONTACTING SUCH EMERGENCY SERVICES.
      </span>
    </p>
    <p>
      <span>
        THE SERVICES AND ALL INTELLECTUAL PROPERTY RIGHTS ARE PROVIDED BY
        CUSTOMERS.AI (AND ITS LICENSORS AND SUPPLIERS) ON AN “AS-IS” BASIS,
        WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING,
        WITHOUT LIMITATION, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
        PARTICULAR PURPOSE, NON-INFRINGEMENT, OR THAT USE OF THE SERVICES WILL
        BE UNINTERRUPTED OR ERROR-FREE. SOME STATES DO NOT ALLOW LIMITATIONS ON
        HOW LONG AN IMPLIED WARRANTY LASTS, SO THE ABOVE LIMITATIONS MAY NOT
        APPLY TO YOU.&nbsp;
      </span>
    </p>
    <p>
      <span>
        TO THE FULLEST EXTENT ALLOWED BY APPLICABLE LAW, UNDER NO CIRCUMSTANCES
        AND UNDER NO LEGAL THEORY (INCLUDING, WITHOUT LIMITATION, TORT,
        CONTRACT, STRICT LIABILITY, OR OTHERWISE) SHALL CUSTOMERS.AI (OR ITS
        LICENSORS OR SUPPLIERS) BE LIABLE TO YOU OR TO ANY OTHER PERSON FOR (A)
        ANY INDIRECT, SPECIAL, INCIDENTAL, OR CONSEQUENTIAL DAMAGES OF ANY KIND,
        INCLUDING DAMAGES FOR LOST PROFITS, LOSS OF GOODWILL, WORK STOPPAGE,
        ACCURACY OF RESULTS, OR COMPUTER FAILURE OR MALFUNCTION, OR (B) ANY
        AMOUNT, IN THE AGGREGATE, IN EXCESS OF THE GREATER OF (I) $100 OR (II)
        THE AMOUNTS PAID BY YOU TO CUSTOMERS.AI IN CONNECTION WITH THE SERVICES
        IN THE TWELVE (12) MONTH PERIOD PRECEDING THE APPLICABLE CLAIM, OR (C)
        ANY MATTER BEYOND OUR REASONABLE CONTROL. SOME STATES DO NOT ALLOW THE
        EXCLUSION OR LIMITATION OF CERTAIN DAMAGES, SO THE ABOVE LIMITATION AND
        EXCLUSIONS MAY NOT APPLY TO YOU.&nbsp;
      </span>
    </p>
    <p>
      <b>Indemnity</b>
    </p>
    <p>
      <span>
        To the fullest extent allowed by applicable law, you agree to indemnify
        and hold the Customers.ai Parties harmless from and against any and all
        claims, liabilities, damages (actual and consequential), costs, losses
        and expenses (including attorneys’ fees) arising from or in any way
        connected to any third party claims relating to (a) your use of the
        Services (including any actions taken by a third party using your
        account), and (b) your violation of these Terms. In the event of such a
        claim, suit, or action (“Claim”), we will attempt to provide notice of
        the Claim to the contact information we have for your account (provided
        that failure to deliver such notice shall not eliminate or reduce your
        indemnification obligations hereunder).&nbsp;
      </span>
    </p>
    <p>
      <b>Assignment</b>
    </p>
    <p>
      <span>
        You may not assign, delegate or transfer these Terms or your rights or
        obligations hereunder, or your Services account, in any way (by
        operation of law or otherwise) without Customers.ai’s prior written
        consent. We may transfer, assign, or delegate these Terms and our rights
        and obligations without consent.&nbsp;
      </span>
    </p>
    <p>
      <b>Choice of Law; Dispute Resolution&nbsp;</b>
    </p>
    <p>
      <span>
        This Agreement shall be governed by Massachusetts law and controlling
        United States federal law, without regard to the choice or conflicts of
        law provisions of any jurisdiction.&nbsp; Except for (a) small claims
        disputes in which we reserve the right to seek to bring an individual
        action in small claims court located in the applicable location of your
        billing address or (b) disputes in which we reserve the right to seek
        injunctive or other equitable relief for the alleged unlawful use of
        Intellectual Property Rights, you waive your rights to a jury trial and
        to have any dispute arising out of or related to this Agreement or our
        Services resolved in court. Instead, in the event of any dispute or
        disagreement related to this Agreement (a “Dispute”), upon the written
        request of either party, the parties will meet for the purpose of
        resolving such dispute. The parties agree to discuss the problem and
        negotiate in good faith without the necessity of any formal proceedings
        related thereto. No formal proceedings for the resolution of such
        dispute may be commenced until either party concludes in good faith that
        resolution through continued negotiation of the matter in issue does not
        appear likely. The parties further agree that all Disputes which cannot
        be settled in through such discussions will be settled by final and
        binding arbitration administered by JAMS pursuant to its Streamlined
        Arbitration Rules &amp; Procedures. Judgement upon the award rendered in
        any such arbitration may be entered in any court having jurisdiction
        thereof, or application may be made to such court for a judicial
        acceptance of the award and enforcement, as the law of such jurisdiction
        may require or allow. The venue for the arbitration shall be in Boston,
        Massachusetts, unless otherwise mutually agreed to by the parties in
        writing. The arbitration panel will consist of a sole arbitrator. In the
        event the parties are unable to agree upon an arbitrator, either party
        may apply to a court of competent jurisdiction for the appointment
        thereof. No person may be appointed as an arbitrator unless he or she is
        independent of all parties, is skilled in the subject matter of the
        Dispute and is not directly or indirectly carrying on or involved in a
        business being carried on in competition with the businesses of the
        parties. The parties and arbitrators shall keep confidential all awards
        in the arbitration, together with all materials in the proceedings
        created for the purpose of the arbitration and all other documents
        produced by another party in the proceedings not otherwise in the public
        domain, save and to the extent that disclosure may be required of a
        party or arbitrator by legal duty, to protect or pursue a legal right or
        to enforce or challenge an award in legal proceedings before a court or
        other judicial authority.
      </span>
      <span>
        The parties hereto agree that any claims or controversies between us
        must be brought against each other on an individual basis only. That
        means neither you and your affiliates on one hand, nor Customers.ai and
        its affiliates on the other hand, can bring a claim as a plaintiff or
        class member in a class action, consolidated action, or representative
        action. The arbitrator cannot combine more than one person’s or entity’s
        claims into a single case, and cannot preside over any consolidated,
        class or representative proceeding (unless we agree otherwise). And, the
        arbitrator’s decision or award in one person’s or entity’s case can only
        impact the person or entity that brought the claim, not other
        Customers.ai’s customers, and cannot be used to decide other disputes
        with other customers.
      </span>
    </p>
    <p>
      <b>Miscellaneous</b>
    </p>
    <p>
      <span>
        No text or information set forth on any other purchase order, preprinted
        form, electronic communication, or document (other than an Order
        Confirmation, if applicable) shall add to or vary the terms and
        conditions of this Agreement, unless otherwise mutually agreed in
        writing by the parties. If any provision of this Agreement is held by a
        court of competent jurisdiction to be invalid or unenforceable, then
        such provision(s) shall be construed, as nearly as possible, to reflect
        the intentions of the invalid or unenforceable provision(s), with all
        other provisions remaining in full force and effect. No joint venture,
        partnership, employment, or agency relationship exists between you and
        Customers.ai as a result of this Agreement or use of the Services,
      </span>
      <span>
        and you do not have any authority of any kind to bind Customers.ai in
        any respect whatsoever. You and Customers.ai agree there are no third
        party beneficiaries intended under this Agreement. The failure of
        Customers.ai to enforce any right or provision in this Agreement shall
        not constitute a waiver of such right or provision unless acknowledged
        and agreed to by Customers.ai in writing. This Agreement, together with
        any applicable Order Confirmation, and any policies, terms and
        conditions incorporated by reference, comprise the entire agreement
        between you and Customers.ai and supersede all prior or contemporaneous
        negotiations, discussions or agreements, whether written or oral,
        between the parties regarding the subject matter contained herein.&nbsp;
        In the event that any of your Users are contractors to your
        organization, you agree that such contractors shall be required by
        written agreement to comply with all applicable restrictions set forth
        in this Agreement, and you shall be fully liable for and indemnify
        Customers.ai with respect to any failure of any contractor to so comply.
      </span>
      <span>
        You will be responsible for paying, withholding, filing, and reporting
        all taxes, duties, and other governmental assessments associated with
        your activity in connection with the Services, provided that
        Customers.ai may, in its sole discretion, do any of the foregoing on
        your behalf or for itself as it sees fit.&nbsp;
      </span>
    </p>
  </div>
);

export const MasterServiceAgreementPage = () => (
  <Template>
    <MasterServiceAgreement />
  </Template>
);
